// CampaignList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCopy,
  FaWhatsapp,
  FaSms,
  FaEnvelope,
  FaSearch,
  FaSyncAlt,
  FaFilter,
  FaBullhorn,
  FaUsers,
  FaPaperPlane,
  FaChartLine,
  FaRupeeSign,
  FaTimes,
  FaRegClock,
} from "react-icons/fa";
import dayjs from "dayjs";
import axios from "axios";
import { apiUrl, authHeaders } from "./config";

const C = {
  brand: "#4f46e5",
  brandLt: "#eef2ff",
  green: "#059669",
  greenLt: "#ecfdf5",
  amber: "#d97706",
  amberLt: "#fffbeb",
  text: "#111827",
  muted: "#6b7280",
  subtle: "#9ca3af",
  border: "#e5e7eb",
  bg: "#f9fafb",
  white: "#ffffff",
  danger: "#ef4444",
  dangerLt: "#fef2f2",
  cyan: "#0891b2",
  cyanLt: "#ecfeff",
};

const CHANNEL_META = {
  WhatsApp: { icon: <FaWhatsapp />, color: "#25d366", bg: "#f0fdf4" },
  SMS: { icon: <FaSms />, color: "#6c757d", bg: "#f9fafb" },
  Email: { icon: <FaEnvelope />, color: "#0d6efd", bg: "#eff6ff" },
};

const TYPE_META = {
  "one-time": { emoji: "📣", color: "#4f46e5", bg: "#eef2ff", label: "One-time" },
  birthday: { emoji: "🎂", color: "#7c3aed", bg: "#f5f3ff", label: "Birthday" },
  anniversary: { emoji: "💍", color: "#be185d", bg: "#fff1f2", label: "Anniversary" },
  checkin: { emoji: "🏨", color: "#0369a1", bg: "#eff6ff", label: "Check-in" },
  checkout: { emoji: "👋", color: "#0f766e", bg: "#f0fdfa", label: "Check-out" },
  profile_completion: { emoji: "👤", color: "#d97706", bg: "#fffbeb", label: "Profile Completion" },
};

const STATUS_META = {
  Scheduled: { bg: "#fef9c3", color: "#92400e", label: "Scheduled" },
  Active: { bg: "#dcfce7", color: "#15803d", label: "Active" },
  Completed: { bg: "#dbeafe", color: "#1d4ed8", label: "Completed" },
  Draft: { bg: "#f3f4f6", color: "#6b7280", label: "Draft" },
  Paused: { bg: "#f3f4f6", color: "#6b7280", label: "Paused" },
  Cancelled: { bg: "#fee2e2", color: "#991b1b", label: "Cancelled" },
};

const normalizeCampaign = (c = {}) => ({
  ...c,
  id: c.campaignid || c.id,
  channel: c.fallback?.channels?.[0] || c.channel || "WhatsApp",
  audience: Number(c.audience_count ?? c.audience ?? 0),
  conversions: Number(c.converted ?? c.conversions ?? 0),
  revenue: Number(c.revenue_actual ?? c.revenue ?? 0),
  sent: Number(c.sent ?? c.total_sent ?? 0),
  createdAt: c.created_at || c.createdAt,
  type: c.campaign_type || c.eventType || "one-time",
  status: c.status || "Draft",
});

const compactNumber = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const getCurrency = () => localStorage.getItem("currencycode") || "₹";

const formatMoney = (value, compact = false) => {
  const amount = Number(value || 0);
  const currency = getCurrency();

  if (compact && Math.abs(amount) >= 1000) {
    return `${currency}${(amount / 1000).toFixed(1)}K`;
  }

  return `${currency}${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const StatusPill = ({ status }) => {
  const s = STATUS_META[status] || STATUS_META.Draft;

  return (
    <span className="eng-campaign-status" style={{ "--status-bg": s.bg, "--status-color": s.color }}>
      {s.label || status}
    </span>
  );
};

const Metric = ({ label, value, tone }) => (
  <div className="eng-campaign-metric">
    <strong style={{ color: tone }}>{value}</strong>
    <span>{label}</span>
  </div>
);

export const ChannelIcon = ({ channel }) => {
  const m = CHANNEL_META[channel] || CHANNEL_META.WhatsApp;
  return <span style={{ color: m.color }}>{m.icon}</span>;
};

const CampaignList = ({ onNavigate }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [channelFilter, setChannelFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");

  const fetchCampaigns = useCallback(async (manualRefresh = false) => {
    if (manualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const res = await axios.get(`${apiUrl}/feedback/campaigns`, authHeaders());
      setCampaigns((res.data || []).map(normalizeCampaign));
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setCampaigns([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns(false);
  }, [fetchCampaigns]);

  const duplicate = async (c) => {
    const payload = {
      name: `${c.name} (Copy)`,
      eventType: c.campaign_type || c.eventType,
      eventDate: c.event_date || c.eventDate,
      filters: c.filters || {},
      steps: c.steps || [],
      fallback: c.fallback || {},
      templateId: c.template_id || c.templateId || "",
      landingPageId: c.landing_page_id || c.landingPageId || "",
      revenueGoal: c.revenue_goal || 0,
      audienceCount: 0,
      totalCredits: 0,
      status: "Draft",
    };

    try {
      const res = await axios.post(`${apiUrl}/feedback/campaigns`, payload, authHeaders());
      setCampaigns((previous) => [normalizeCampaign({ ...res.data, id: res.data.campaignid }), ...previous]);
    } catch (error) {
      console.error("Error duplicating campaign:", error);
      setCampaigns((previous) => [normalizeCampaign({ ...payload, id: `c${Date.now()}` }), ...previous]);
    }
  };

  const deleteCampaign = async () => {
    if (!confirmId) return;

    setDeleting(true);

    try {
      await axios.delete(`${apiUrl}/feedback/campaigns/${confirmId}`, authHeaders());
    } catch (error) {
      console.error("Error deleting campaign:", error);
    }

    setCampaigns((previous) => previous.filter((c) => (c.campaignid || c.id) !== confirmId));
    setConfirmId(null);
    setConfirmName("");
    setDeleting(false);
  };

  const channelOptions = useMemo(() => {
    const unique = Array.from(new Set(campaigns.map((c) => c.channel).filter(Boolean)));
    return ["All", ...unique.sort()];
  }, [campaigns]);

  const typeOptions = useMemo(() => {
    const unique = Array.from(new Set(campaigns.map((c) => c.type).filter(Boolean)));
    return ["All", ...unique.sort()];
  }, [campaigns]);

  const filteredCampaigns = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    return campaigns.filter((c) => {
      const matchesSearch =
        !q ||
        `${c.name || ""} ${c.status || ""} ${c.channel || ""} ${c.type || ""}`
          .toLowerCase()
          .includes(q);

      const matchesStatus = statusFilter === "All" || c.status === statusFilter;
      const matchesChannel = channelFilter === "All" || c.channel === channelFilter;
      const matchesType = typeFilter === "All" || c.type === typeFilter;

      return matchesSearch && matchesStatus && matchesChannel && matchesType;
    });
  }, [campaigns, searchText, statusFilter, channelFilter, typeFilter]);

  const stats = useMemo(() => {
    const active = campaigns.filter((c) => c.status === "Active").length;
    const scheduled = campaigns.filter((c) => c.status === "Scheduled").length;
    const audience = campaigns.reduce((sum, c) => sum + Number(c.audience || 0), 0);
    const sent = campaigns.reduce((sum, c) => sum + Number(c.sent || 0), 0);
    const revenue = campaigns.reduce((sum, c) => sum + Number(c.revenue || 0), 0);

    return {
      total: campaigns.length,
      active,
      scheduled,
      audience,
      sent,
      revenue,
    };
  }, [campaigns]);

  const clearFilters = () => {
    setSearchText("");
    setStatusFilter("All");
    setChannelFilter("All");
    setTypeFilter("All");
  };

  if (loading) {
    return (
      <div className="eng-campaign-root">
        <style>{campaignStyles}</style>
        <div className="eng-campaign-loader">
          <div className="eng-campaign-dots">
            <span />
            <span />
            <span />
            <span />
          </div>
          <strong>Loading campaigns...</strong>
          <p>Preparing broadcasts, audience totals, delivery status, and revenue movement.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="eng-campaign-root">
      <style>{campaignStyles}</style>

      <section className="eng-campaign-hero">
        <div className="eng-campaign-hero-left">
          <span className="eng-campaign-hero-icon">
            <FaBullhorn />
          </span>

          <div>
            <div className="eng-campaign-eyebrow">Guest Broadcasts</div>
            <h2>Campaigns</h2>
            <p>Plan, duplicate, and track one-time guest broadcasts for promotions, offers, and announcements.</p>
          </div>
        </div>

        <div className="eng-campaign-hero-actions">
          <button
            type="button"
            className="eng-campaign-soft-btn"
            onClick={() => fetchCampaigns(true)}
            disabled={refreshing}
          >
            <FaSyncAlt className={refreshing ? "eng-spin" : ""} />
            {refreshing ? "Refreshing" : "Refresh"}
          </button>

          <button
            type="button"
            className="eng-campaign-primary-btn"
            onClick={() => onNavigate("campaigns-create")}
          >
            <FaPlus />
            New Campaign
          </button>
        </div>
      </section>

      <section className="eng-campaign-stat-grid">
        <article className="eng-campaign-stat" style={{ "--tone": C.brand }}>
          <span>Total Campaigns</span>
          <strong>{compactNumber(stats.total)}</strong>
        </article>
        <article className="eng-campaign-stat" style={{ "--tone": C.green }}>
          <span>Active</span>
          <strong>{compactNumber(stats.active)}</strong>
        </article>
        <article className="eng-campaign-stat" style={{ "--tone": C.amber }}>
          <span>Scheduled</span>
          <strong>{compactNumber(stats.scheduled)}</strong>
        </article>
        <article className="eng-campaign-stat" style={{ "--tone": C.cyan }}>
          <span>Total Audience</span>
          <strong>{compactNumber(stats.audience)}</strong>
        </article>
        <article className="eng-campaign-stat" style={{ "--tone": "#7c3aed" }}>
          <span>Total Sent</span>
          <strong>{compactNumber(stats.sent)}</strong>
        </article>
        <article className="eng-campaign-stat" style={{ "--tone": C.green }}>
          <span>Revenue</span>
          <strong>{formatMoney(stats.revenue, true)}</strong>
        </article>
      </section>

      <section className="eng-campaign-toolbar">
        <div className="eng-campaign-search">
          <FaSearch />
          <input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search campaign name, status, channel, or type..."
          />
        </div>

        <div className="eng-campaign-filter-group">
          <span className="eng-campaign-filter-label">
            <FaFilter />
            Filters
          </span>

          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {["All", "Scheduled", "Active", "Completed", "Draft", "Paused", "Cancelled"].map((status) => (
              <option key={status} value={status}>
                {status === "All" ? "All Status" : status}
              </option>
            ))}
          </select>

          <select value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
            {channelOptions.map((channel) => (
              <option key={channel} value={channel}>
                {channel === "All" ? "All Channels" : channel}
              </option>
            ))}
          </select>

          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            {typeOptions.map((type) => (
              <option key={type} value={type}>
                {type === "All" ? "All Types" : TYPE_META[type]?.label || type}
              </option>
            ))}
          </select>

          {(searchText || statusFilter !== "All" || channelFilter !== "All" || typeFilter !== "All") && (
            <button type="button" className="eng-campaign-clear-btn" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
      </section>

      {campaigns.length === 0 ? (
        <section className="eng-campaign-empty">
          <span className="eng-campaign-empty-icon">
            <FaBullhorn />
          </span>
          <strong>No campaigns yet</strong>
          <p>
            Create a campaign to send promotions, birthday offers, anniversary packages, and announcements directly to
            your guests.
          </p>
          <button type="button" className="eng-campaign-primary-btn" onClick={() => onNavigate("campaigns-create")}>
            <FaPlus />
            Create First Campaign
          </button>
        </section>
      ) : filteredCampaigns.length === 0 ? (
        <section className="eng-campaign-empty">
          <span className="eng-campaign-empty-icon">
            <FaSearch />
          </span>
          <strong>No campaigns match the filters</strong>
          <p>Adjust search, status, channel, or campaign type filters to view more campaigns.</p>
          <button type="button" className="eng-campaign-soft-btn" onClick={clearFilters}>
            Clear Filters
          </button>
        </section>
      ) : (
        <section className="eng-campaign-grid">
          {filteredCampaigns.map((c) => {
            const id = c.campaignid || c.id;
            const typeMeta = TYPE_META[c.type] || TYPE_META["one-time"];
            const chMeta = CHANNEL_META[c.channel] || CHANNEL_META.WhatsApp;
            const createdAt = c.createdAt || c.created_at;
            const conversionRate = c.audience ? Math.round((Number(c.conversions || 0) / Number(c.audience || 1)) * 100) : 0;

            return (
              <article
                key={id}
                className="eng-campaign-card"
                style={{ "--type-color": typeMeta.color, "--type-bg": typeMeta.bg }}
              >
                <div className="eng-campaign-card-top">
                  <span className="eng-campaign-type-icon">{typeMeta.emoji}</span>
                  <StatusPill status={c.status} />
                </div>

                <h3 title={c.name}>{c.name || "Untitled Campaign"}</h3>

                <div className="eng-campaign-meta-row">
                  <span className="eng-campaign-channel-pill" style={{ "--channel-bg": chMeta.bg, "--channel-color": chMeta.color }}>
                    {chMeta.icon}
                    {c.channel || "WhatsApp"}
                  </span>

                  <span className="eng-campaign-type-pill">
                    {TYPE_META[c.type]?.label || c.type || "One-time"}
                  </span>
                </div>

                <div className="eng-campaign-metrics">
                  <Metric label="Audience" value={compactNumber(c.audience || 0)} tone={C.text} />
                  <Metric label="Sent" value={compactNumber(c.sent || 0)} tone={C.muted} />
                  <Metric label="Revenue" value={formatMoney(c.revenue || 0, true)} tone={C.green} />
                </div>

                <div className="eng-campaign-foot">
                  <span>
                    <FaChartLine />
                    {conversionRate}% conversion
                  </span>
                  <span>
                    <FaRegClock />
                    {createdAt ? dayjs(createdAt).format("DD MMM YYYY") : "Not scheduled"}
                  </span>
                </div>

                <div className="eng-campaign-actions" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    title="Edit campaign"
                    onClick={() => onNavigate("campaigns-edit", c)}
                    className="eng-campaign-icon-btn brand"
                  >
                    <FaEdit />
                  </button>

                  <button
                    type="button"
                    title="Duplicate campaign"
                    onClick={() => duplicate(c)}
                    className="eng-campaign-icon-btn cyan"
                  >
                    <FaCopy />
                  </button>

                  <button
                    type="button"
                    title="Delete campaign"
                    onClick={() => {
                      setConfirmId(id);
                      setConfirmName(c.name || "Untitled Campaign");
                    }}
                    className="eng-campaign-icon-btn danger"
                  >
                    <FaTrash />
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      )}

      {confirmId && (
        <div className="eng-campaign-delete-root" role="dialog" aria-modal="true" aria-label="Delete campaign">
          <button
            type="button"
            className="eng-campaign-delete-backdrop"
            onClick={() => {
              setConfirmId(null);
              setConfirmName("");
            }}
            aria-label="Cancel delete"
          />

          <div className="eng-campaign-delete-card">
            <div className="eng-campaign-delete-icon">
              <FaTrash />
            </div>

            <div className="eng-campaign-delete-copy">
              <strong>Delete Campaign?</strong>
              <p>
                <b>{confirmName}</b> will be permanently deleted. Scheduled sends and campaign reporting history may no
                longer be available from this list.
              </p>
            </div>

            <div className="eng-campaign-delete-actions">
              <button
                type="button"
                className="eng-campaign-soft-btn"
                onClick={() => {
                  setConfirmId(null);
                  setConfirmName("");
                }}
                disabled={deleting}
              >
                <FaTimes />
                Cancel
              </button>

              <button
                type="button"
                className="eng-campaign-danger-btn"
                onClick={deleteCampaign}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete Campaign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const campaignStyles = `
  .eng-campaign-root {
    min-height: 100%;
    padding: 18px 22px 28px;
    background:
      radial-gradient(circle at 7% 0%, rgba(79, 70, 229, 0.08), transparent 28%),
      radial-gradient(circle at 92% 3%, rgba(5, 150, 105, 0.08), transparent 30%),
      #f8fafc;
    color: ${C.text};
  }

  .eng-campaign-hero {
    background:
      radial-gradient(circle at top left, rgba(79, 70, 229, 0.14), transparent 34%),
      radial-gradient(circle at top right, rgba(5, 150, 105, 0.10), transparent 32%),
      linear-gradient(135deg, #ffffff 0%, #fbfdff 100%);
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 24px;
    box-shadow: 0 16px 42px rgba(17, 24, 39, 0.075);
    padding: 17px 18px;
    margin-bottom: 12px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
  }

  .eng-campaign-hero-left {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    min-width: 0;
  }

  .eng-campaign-hero-icon,
  .eng-campaign-empty-icon {
    width: 52px;
    height: 52px;
    border-radius: 19px;
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 16px 34px rgba(79, 70, 229, 0.22);
    flex: 0 0 auto;
    font-size: 1.25rem;
  }

  .eng-campaign-eyebrow {
    color: #4f46e5;
    font-size: 0.72rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-weight: 950;
    margin-bottom: 5px;
  }

  .eng-campaign-hero h2 {
    color: #111827;
    font-size: 1.42rem;
    line-height: 1.16;
    font-weight: 950;
    margin: 0;
  }

  .eng-campaign-hero p {
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.5;
    font-weight: 760;
    margin: 7px 0 0;
    max-width: 780px;
  }

  .eng-campaign-hero-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }

  .eng-campaign-primary-btn,
  .eng-campaign-soft-btn,
  .eng-campaign-danger-btn {
    min-height: 40px;
    border-radius: 14px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 15px;
    font-size: 0.82rem;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
    transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
  }

  .eng-campaign-primary-btn {
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    border-color: #4f46e5;
    color: #ffffff;
    box-shadow: 0 14px 30px rgba(79, 70, 229, 0.18);
  }

  .eng-campaign-soft-btn {
    background: #ffffff;
    color: #334155;
  }

  .eng-campaign-danger-btn {
    background: linear-gradient(135deg, #dc2626, #ef4444);
    border-color: #dc2626;
    color: #ffffff;
    box-shadow: 0 14px 30px rgba(220, 38, 38, 0.18);
  }

  .eng-campaign-primary-btn:hover,
  .eng-campaign-soft-btn:hover,
  .eng-campaign-danger-btn:hover {
    transform: translateY(-1px);
  }

  .eng-campaign-primary-btn:disabled,
  .eng-campaign-soft-btn:disabled,
  .eng-campaign-danger-btn:disabled {
    opacity: 0.62;
    cursor: not-allowed;
    transform: none;
  }

  .eng-spin {
    animation: eng-spin 0.9s linear infinite;
  }

  @keyframes eng-spin {
    to { transform: rotate(360deg); }
  }

  .eng-campaign-stat-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }

  .eng-campaign-stat {
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-top: 4px solid var(--tone);
    border-radius: 20px;
    box-shadow: 0 12px 30px rgba(17, 24, 39, 0.06);
    padding: 13px 14px;
    min-width: 0;
  }

  .eng-campaign-stat span {
    color: #64748b;
    display: block;
    font-size: 0.66rem;
    font-weight: 950;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 7px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .eng-campaign-stat strong {
    color: #0f172a;
    display: block;
    font-size: 1.18rem;
    line-height: 1.12;
    font-weight: 950;
  }

  .eng-campaign-toolbar {
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 22px;
    box-shadow: 0 12px 30px rgba(17, 24, 39, 0.06);
    padding: 12px;
    margin-bottom: 12px;
    display: grid;
    grid-template-columns: minmax(260px, 1fr) auto;
    gap: 10px;
    align-items: center;
  }

  .eng-campaign-search {
    min-height: 42px;
    border-radius: 15px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #ffffff;
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 0 12px;
  }

  .eng-campaign-search svg {
    color: #94a3b8;
    flex: 0 0 auto;
  }

  .eng-campaign-search input {
    width: 100%;
    min-height: 40px;
    border: 0;
    outline: none;
    color: #0f172a;
    font-size: 0.84rem;
    font-weight: 760;
  }

  .eng-campaign-search input::placeholder {
    color: #94a3b8;
  }

  .eng-campaign-filter-group {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .eng-campaign-filter-label {
    min-height: 38px;
    border-radius: 999px;
    background: #f8fafc;
    color: #64748b;
    border: 1px solid rgba(17, 24, 39, 0.08);
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 11px;
    font-size: 0.74rem;
    font-weight: 950;
  }

  .eng-campaign-filter-group select {
    min-height: 40px;
    min-width: 142px;
    border-radius: 14px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #ffffff;
    color: #0f172a;
    padding: 0 10px;
    font-size: 0.82rem;
    font-weight: 850;
    outline: none;
  }

  .eng-campaign-clear-btn {
    min-height: 40px;
    border-radius: 14px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #fff;
    color: #4f46e5;
    padding: 0 12px;
    font-size: 0.78rem;
    font-weight: 950;
    cursor: pointer;
  }

  .eng-campaign-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(286px, 1fr));
    gap: 12px;
  }

  .eng-campaign-card {
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-top: 4px solid var(--type-color);
    border-radius: 22px;
    box-shadow: 0 12px 30px rgba(17, 24, 39, 0.06);
    padding: 14px;
    min-height: 246px;
    display: flex;
    flex-direction: column;
    gap: 11px;
    transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
  }

  .eng-campaign-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 42px rgba(17, 24, 39, 0.11);
  }

  .eng-campaign-card-top,
  .eng-campaign-meta-row,
  .eng-campaign-foot,
  .eng-campaign-actions {
    display: flex;
    align-items: center;
  }

  .eng-campaign-card-top,
  .eng-campaign-foot {
    justify-content: space-between;
    gap: 10px;
  }

  .eng-campaign-type-icon {
    width: 44px;
    height: 44px;
    border-radius: 16px;
    background: var(--type-bg);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.28rem;
    flex: 0 0 auto;
  }

  .eng-campaign-status {
    min-height: 28px;
    border-radius: 999px;
    background: var(--status-bg);
    color: var(--status-color);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    font-size: 0.68rem;
    font-weight: 950;
    white-space: nowrap;
  }

  .eng-campaign-card h3 {
    color: #111827;
    font-size: 0.96rem;
    line-height: 1.28;
    font-weight: 950;
    margin: 0;
    min-height: 38px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .eng-campaign-meta-row {
    gap: 7px;
    color: #64748b;
    font-size: 0.74rem;
    font-weight: 800;
    flex-wrap: wrap;
  }

  .eng-campaign-channel-pill,
  .eng-campaign-type-pill {
    min-height: 24px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 8px;
    font-size: 0.68rem;
    font-weight: 950;
  }

  .eng-campaign-channel-pill {
    background: var(--channel-bg);
    color: var(--channel-color);
  }

  .eng-campaign-type-pill {
    background: var(--type-bg);
    color: var(--type-color);
  }

  .eng-campaign-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border-top: 1px solid rgba(17, 24, 39, 0.08);
    border-bottom: 1px solid rgba(17, 24, 39, 0.08);
    padding: 10px 0;
    gap: 0;
  }

  .eng-campaign-metric {
    text-align: center;
    border-left: 1px solid rgba(17, 24, 39, 0.08);
    min-width: 0;
  }

  .eng-campaign-metric:first-child {
    border-left: 0;
  }

  .eng-campaign-metric strong {
    display: block;
    font-size: 0.94rem;
    line-height: 1.1;
    font-weight: 950;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .eng-campaign-metric span {
    display: block;
    color: #94a3b8;
    font-size: 0.58rem;
    font-weight: 950;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-top: 4px;
  }

  .eng-campaign-foot {
    color: #64748b;
    font-size: 0.68rem;
    font-weight: 850;
    margin-top: auto;
  }

  .eng-campaign-foot span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .eng-campaign-actions {
    gap: 7px;
    margin-top: 2px;
  }

  .eng-campaign-icon-btn {
    flex: 1;
    min-height: 36px;
    border-radius: 12px;
    border: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.15s ease, opacity 0.15s ease;
  }

  .eng-campaign-icon-btn:hover {
    transform: translateY(-1px);
  }

  .eng-campaign-icon-btn.brand { background: #eef2ff; color: #4f46e5; }
  .eng-campaign-icon-btn.cyan { background: #ecfeff; color: #0891b2; }
  .eng-campaign-icon-btn.danger { background: #fef2f2; color: #ef4444; }

  .eng-campaign-empty,
  .eng-campaign-loader {
    min-height: 360px;
    background: #ffffff;
    border: 1px dashed #cbd5e1;
    border-radius: 24px;
    box-shadow: 0 16px 42px rgba(17, 24, 39, 0.06);
    padding: 40px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    text-align: center;
  }

  .eng-campaign-empty strong,
  .eng-campaign-loader strong {
    color: #111827;
    font-size: 1.02rem;
    font-weight: 950;
  }

  .eng-campaign-empty p,
  .eng-campaign-loader p {
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.48;
    font-weight: 760;
    max-width: 430px;
    margin: 0 0 8px;
  }

  .eng-campaign-dots {
    display: flex;
    gap: 8px;
    margin-bottom: 4px;
  }

  .eng-campaign-dots span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #4f46e5;
    animation: eng-campaign-bounce 0.58s infinite alternate;
  }

  .eng-campaign-dots span:nth-child(2) { animation-delay: 0.1s; }
  .eng-campaign-dots span:nth-child(3) { animation-delay: 0.2s; }
  .eng-campaign-dots span:nth-child(4) { animation-delay: 0.3s; }

  @keyframes eng-campaign-bounce {
    to { transform: translateY(-10px); }
  }

  .eng-campaign-delete-root {
    position: fixed;
    inset: 0;
    z-index: 2147483000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px;
    background: rgba(15, 23, 42, 0.10);
    backdrop-filter: blur(1.5px);
    -webkit-backdrop-filter: blur(1.5px);
  }

  .eng-campaign-delete-backdrop {
    position: absolute;
    inset: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
  }

  .eng-campaign-delete-card {
    position: relative;
    width: min(440px, 100%);
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 24px;
    box-shadow: 0 24px 70px rgba(17, 24, 39, 0.22);
    padding: 22px;
    display: grid;
    gap: 14px;
  }

  .eng-campaign-delete-icon {
    width: 54px;
    height: 54px;
    border-radius: 20px;
    background: #fef2f2;
    color: #ef4444;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
  }

  .eng-campaign-delete-copy strong {
    color: #111827;
    display: block;
    font-size: 1.08rem;
    font-weight: 950;
  }

  .eng-campaign-delete-copy p {
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.48;
    font-weight: 760;
    margin: 6px 0 0;
  }

  .eng-campaign-delete-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }

  @media (max-width: 1320px) {
    .eng-campaign-stat-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .eng-campaign-toolbar {
      grid-template-columns: 1fr;
    }

    .eng-campaign-filter-group {
      justify-content: flex-start;
    }
  }

  @media (max-width: 720px) {
    .eng-campaign-root {
      padding: 12px 10px 22px;
    }

    .eng-campaign-hero-actions,
    .eng-campaign-filter-group {
      width: 100%;
    }

    .eng-campaign-primary-btn,
    .eng-campaign-soft-btn,
    .eng-campaign-danger-btn,
    .eng-campaign-filter-group select,
    .eng-campaign-clear-btn {
      width: 100%;
    }

    .eng-campaign-stat-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .eng-campaign-delete-actions {
      display: grid;
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 520px) {
    .eng-campaign-stat-grid,
    .eng-campaign-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export default CampaignList;
